import { useEffect, useMemo, useRef } from 'react';
import { Box, FormControl, FormHelperText, TextField, Typography } from '@material-ui/core';
import { Autocomplete, Alert } from '@material-ui/lab';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type UiOptions = {
  /**
   * Field name in the scaffolder formData that contains the Step Function definition JSON string.
   * Defaults to "step_function_definition_json".
   */
  definitionFieldName?: string;
  /**
   * Single-select only: if the current value equals this string and we detect exactly one MapRun
   * label, we auto-select it.
   */
  autoReplaceIfValueIs?: string;
};

const DEFAULT_DEFINITION_FIELD = 'step_function_definition_json';

function parseMapRunLabels(definitionText: string): { labels: string[]; parseError?: string } {
  try {
    const parsed = JSON.parse(definitionText);
    const states = parsed?.States;
    if (!states || typeof states !== 'object') return { labels: [] };

    const labels: string[] = [];
    for (const [stateName, stateDef] of Object.entries(states as Record<string, any>)) {
      if (!stateDef || typeof stateDef !== 'object') continue;
      if (stateDef.Type !== 'Map') continue;

      // We only care about MapRun patterns (Distributed Map)
      const mode = stateDef?.ItemProcessor?.ProcessorConfig?.Mode;
      if (mode !== 'DISTRIBUTED') continue;

      const label =
        typeof stateDef.Label === 'string' && stateDef.Label.trim()
          ? stateDef.Label.trim()
          : String(stateName);

      labels.push(label);
    }

    return { labels: [...new Set(labels)] };
  } catch (e) {
    return { labels: [], parseError: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Detects Distributed Map ("MapRun") state labels in the definition and prepopulates the field the
 * step-function alarms module keys on.
 *
 * One implementation, two scaffolder field extensions: `map_state_name` takes a single label,
 * `map_state_names` takes several (one alarms module instance per selected label). Only the value
 * type, the auto-populate rule and the copy differ — the parser and the layout are shared. The two
 * extension names are what templates reference, so they stay distinct.
 */
function MapRunAutoDetect(
  props:
    | (FieldExtensionComponentProps<string, UiOptions> & { multiple?: false })
    | (FieldExtensionComponentProps<string[], UiOptions> & { multiple: true }),
) {
  const { onChange, schema, uiSchema, formData, formContext, multiple } = props as any;
  const title =
    schema.title || uiSchema?.['ui:title'] || (multiple ? 'Map State Names' : 'Map State Name');
  const description =
    schema.description ||
    uiSchema?.['ui:description'] ||
    (multiple
      ? 'Detected Distributed Map labels from your Step Function definition. Select one or more.'
      : 'Detected from your Step Function definition (uses the Map state "Label" when present).');

  const definitionFieldName =
    uiSchema?.['ui:options']?.definitionFieldName ?? DEFAULT_DEFINITION_FIELD;
  const autoReplaceIfValueIs = uiSchema?.['ui:options']?.autoReplaceIfValueIs ?? 'MapState';

  const rootFormData = (formContext as any)?.formData as Record<string, unknown> | undefined;
  const definitionText = String(rootFormData?.[definitionFieldName] ?? '');

  const single = typeof formData === 'string' ? formData : '';
  const many = useMemo(() => (Array.isArray(formData) ? formData : []), [formData]);

  const detected = useMemo(() => {
    if (!definitionText.trim()) return { labels: [] as string[] };
    return parseMapRunLabels(definitionText);
  }, [definitionText]);

  const userTouchedRef = useRef(false);
  const lastDetectedKeyRef = useRef<string>('');

  // Auto-populate once per distinct detection, and never over a value the user typed:
  // multi -> fill an empty field with everything detected; single -> only replace the placeholder
  // default, and only when the detection is unambiguous.
  useEffect(() => {
    const key = detected.labels.slice().sort().join('\n');
    if (key === lastDetectedKeyRef.current) return;
    lastDetectedKeyRef.current = key;

    if (userTouchedRef.current) return;

    if (multiple) {
      if (many.length === 0 && detected.labels.length > 0) onChange(detected.labels);
    } else if (single === autoReplaceIfValueIs && detected.labels.length === 1) {
      onChange(detected.labels[0]);
    }
  }, [autoReplaceIfValueIs, detected.labels, many, multiple, onChange, single]);

  return (
    <FormControl fullWidth>
      <Box mb={1}>
        <Typography variant="subtitle2">{title}</Typography>
        {description && <FormHelperText>{description}</FormHelperText>}
      </Box>

      {definitionText.trim() && detected.parseError && (
        <Alert severity="info" style={{ marginBottom: 8 }}>
          Unable to auto-detect MapRun label(s) because the definition JSON is not valid yet (
          {detected.parseError}).
        </Alert>
      )}

      {definitionText.trim() && !detected.parseError && detected.labels.length === 0 && (
        <Alert severity="info" style={{ marginBottom: 8 }}>
          No Distributed Map ("MapRun") states detected in the definition.
          {!multiple && ' If you enable alarms anyway, you must enter the Map label/name manually.'}
        </Alert>
      )}

      {detected.labels.length > 0 && (
        <Alert severity="success" style={{ marginBottom: 8 }}>
          Detected MapRun label{detected.labels.length === 1 ? '' : 's'}:{' '}
          {detected.labels.join(', ')}
        </Alert>
      )}

      {multiple ? (
        <Autocomplete
          multiple
          freeSolo
          options={detected.labels}
          value={many}
          onChange={(_e, newValue) => {
            userTouchedRef.current = true;
            onChange((newValue ?? []).map(v => String(v)).filter(Boolean));
          }}
          renderInput={params => (
            <TextField {...params} variant="outlined" placeholder="FileAnalysis" />
          )}
        />
      ) : (
        <Autocomplete
          freeSolo
          options={detected.labels}
          value={single}
          onChange={(_e, newValue) => {
            userTouchedRef.current = true;
            onChange(String(newValue ?? ''));
          }}
          onInputChange={(_e, newInputValue) => {
            userTouchedRef.current = true;
            onChange(newInputValue);
          }}
          renderInput={params => (
            <TextField {...params} variant="outlined" placeholder="FileAnalysis" />
          )}
        />
      )}
    </FormControl>
  );
}

export const MapRunStateNameAutoDetect = (
  props: FieldExtensionComponentProps<string, UiOptions>,
) => <MapRunAutoDetect {...props} />;

export const MapRunStateNamesAutoDetect = (
  props: FieldExtensionComponentProps<string[], UiOptions>,
) => <MapRunAutoDetect {...props} multiple />;

// Field extension registration moved to plugin.ts following Backstage best practices
